import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Box, Typography, CircularProgress } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { saveToken, clearToken, useAuth } from '@contexts/AuthContext';
import { ROLE_INTERNAL_API } from '@utils/helpers';

function AuthPage() {
  const { t } = useTranslation();
  const { refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleAuth = async () => {
      try {
        const token = searchParams.get('token');
        
        if (!token) {
          setError(t('No token provided'));
          setIsLoading(false);
          return;
        }

        // Valider le token en le décodant
        let decoded;
        try {
          decoded = jwtDecode(token);
        } catch (decodeError) {
          setError(t('Invalid token format'));
          setIsLoading(false);
          return;
        }

        // Vérifier si le token n'est pas expiré
        const currentTime = Date.now() / 1000;
        if (decoded.exp < currentTime) {
          setError(t('Token has expired'));
          setIsLoading(false);
          return;
        }

        // Vérifier que ce n'est pas un compte API interne
        if (decoded.role === ROLE_INTERNAL_API) {
          setError(t('This account cannot be used to log in'));
          setIsLoading(false);
          return;
        }

        // Effacer l'ancien token s'il y en avait un
        clearToken();
        
        // Sauvegarder le nouveau token
        saveToken(token);

        // Refresh du token pour l'invalider immédiatement
        // (on l'a reçu via l'url ce qui n'est pas toujours secure)
        refreshAuth(true);

        // Rediriger vers la page d'accueil
        navigate('/', { replace: true });
        
      } catch (err) {
        console.error('Auth error:', err);
        setError(t('Authentication failed'));
        setIsLoading(false);
      }
    };

    handleAuth();
  }, [searchParams, navigate, t, refreshAuth]);

  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          gap={2}
        >
          <CircularProgress />
          <Typography variant="body1">
            {t('Authenticating...')}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 6 }}>
        <Typography variant="h1" align="center">
          {t('Authentication failed')}
        </Typography>
        <Typography variant="body1" align="center">
          {error}
        </Typography>
        <Typography variant="body1" align="center" sx={{ mt: 2 }}>
          {t('Please close this page and try logging in again')}.
        </Typography>
      </Container>
    );
  }

  return null;
}

export default AuthPage; 